import React, { useEffect, useRef } from 'react';
import { Helmet } from "react-helmet-async";
import {
    Home,
    Building2,
    Compass,
    Sun,
    Layers,
    Box,
    Sliders,
    Maximize2,
    Sparkles,
    Eye,
    Grid,
    Image as ImageIcon
} from 'lucide-react';

const interiorServices = [
    { id: '01', icon: Home, title: 'Luxury Residential Editorials', desc: 'Crafting atmospheric, magazine-ready narratives for interior designers and luxury home builders, focusing on tactile textures and lived-in styling.' },
    { id: '02', icon: Building2, title: 'Commercial & Corporate Architecture', desc: 'Clean, large-scale spatial captures for hospitality, retail, and workplace environments designed to showcase brand scale, layout flow, and operational utility.' },
    { id: '03', icon: Compass, title: 'Spatial Flow & Vignettes', desc: 'Isolating intimate design corners, micro-curations, unique joinery, bespoke furniture, and material intersections with shallow depth of field.' },
    { id: '04', icon: Sun, title: 'Daylight & Golden-Hour Ambiance', desc: 'Tracking precise sun placement to capture rooms flooded with organic light shadows, showcasing how spaces naturally transform throughout the day.' },
    { id: '05', icon: Maximize2, title: 'Drone & Aerial Contextuals', desc: 'High-resolution aerial perspective framing to visually connect luxury structures and sprawling estates gracefully to their surrounding landscape.' },
    { id: '06', icon: Sparkles, title: 'Twilight & Hospitality Lighting', desc: 'Balancing ambient dark blue hour skylines with complex internal accent warm lights to turn resorts, hotels, and restaurants into majestic night scenes.' },
];

const interiorDeliverables = [
    { id: '01', icon: Grid, title: 'Perspective Parallax & Vertical Correction', desc: 'Utilizing specialized optics and digital adjustments to eliminate natural wide-angle lens keystoning, keeping wall lines perfectly parallel.' },
    { id: '02', icon: Layers, title: 'Ambient-Strobe Exposure Compositing', desc: 'Meticulously hand-blending natural window light with subtle hidden studio flashes to deliver realistic view exposures outside without muddying shadows.' },
    { id: '03', icon: Sun, title: 'Strict Color-Temperature Uniformity', desc: 'Neutralizing clashing white balances caused by mixing yellow incandescent interior lamps with blue window daylight for smooth, real tonal ranges.' },
    { id: '04', icon: Sliders, title: 'Non-Destructive Digital Decluttering', desc: 'Polishing high-end frames on a pixel level to remove distracting electrical wall outlets, floor wires, ceiling smoke detectors, or scuffs.' },
    { id: '05', icon: Compass, title: 'Styling & Micro-Placement Consulting', desc: 'Collaborating on-set to slightly adjust furniture tracking angles, drape orientations, and props to optimize balance for the camera lens frame.' },
    { id: '06', icon: Box, title: 'Ultra-Wide Low-Distortion Metrics', desc: 'Deploying high-fidelity lenses that capture expansive tight spaces fully without introducing ugly fisheye or cartoonish corner warping.' },
    { id: '07', icon: Sparkles, title: 'Material Texture Optimization', desc: 'Sculpting specialized specular lighting highlights over marble slabs, brushed metals, rich velvet fabrics, and natural wood grains.' },
    { id: '08', icon: Eye, title: 'Pre-Production Lighting Path Mapping', desc: 'Utilizing sun-tracking radar algorithms ahead of time to precisely project optimal shooting hours for every individual room layout.' },
    { id: '09', icon: ImageIcon, title: 'Redundant Archival Raw Storage', desc: 'Preserving giant, high-dynamic-range layered structural data across secure localized and cloud servers for print billboard layout adjustments.' },
];

const InteriorPhotographyPortfolio = () => {
    const viewportRef = useRef(null);
    const leftStripRef = useRef(null);
    const rightStripRef = useRef(null);

    useEffect(() => {
        const viewport = viewportRef.current;
        const leftStrip = leftStripRef.current;
        const rightStrip = rightStripRef.current;

        if (!viewport || !leftStrip || !rightStrip) return;

        let currentPosLeft = 0;
        let currentPosRight = 0;
        let isHovered = false;
        let animationFrameId;

        const speedLeft = 0.45;
        const speedRight = 0.25;

        const handleMouseEnter = () => { isHovered = true; };
        const handleMouseLeave = () => { isHovered = false; };

        viewport.addEventListener('mouseenter', handleMouseEnter);
        viewport.addEventListener('mouseleave', handleMouseLeave);

        function performAutoScroll() {
            if (!isHovered && leftStrip && rightStrip) {
                currentPosLeft -= speedLeft;
                currentPosRight -= speedRight;

                if (Math.abs(currentPosLeft) >= (leftStrip.scrollHeight / 2)) {
                    currentPosLeft = 0;
                }
                if (Math.abs(currentPosRight) >= (rightStrip.scrollHeight / 2)) {
                    currentPosRight = 0;
                }

                if (window.innerWidth > 991) {
                    leftStrip.style.transform = `translateY(${currentPosLeft}px)`;
                    rightStrip.style.transform = `translateY(${currentPosRight}px)`;
                } else {
                    leftStrip.style.transform = 'none';
                    rightStrip.style.transform = 'none';
                }
            }
            animationFrameId = requestAnimationFrame(performAutoScroll);
        }

        animationFrameId = requestAnimationFrame(performAutoScroll);

        return () => {
            cancelAnimationFrame(animationFrameId);
            if (viewport) {
                viewport.removeEventListener('mouseenter', handleMouseEnter);
                viewport.removeEventListener('mouseleave', handleMouseLeave);
            }
        };
    }, []);

    return (
        <div>
            <Helmet>
                <title>Interior Photography Portfolio in Hyderabad | KhultStudios</title>

                <meta
                    name="description"
                    content="Explore KhultStudios' Interior Photography Portfolio featuring luxury homes, apartments, villas, offices, hotels, restaurants, retail stores, architecture, real estate, and commercial interior photography in Hyderabad."
                />

                <meta
                    name="keywords"
                    content="Interior Photography Portfolio Hyderabad, Interior Photography Hyderabad, Architectural Photography Hyderabad, Real Estate Photography, Home Interior Photography, Villa Photography, Apartment Photography, Office Photography, Hotel Photography, Restaurant Photography, Retail Store Photography, Commercial Interior Photography, Property Photography Hyderabad, Architecture Photography, KhultStudios"
                />

                <meta name="robots" content="index, follow" />
                <meta name="author" content="KhultStudios" />

                <link
                    rel="canonical"
                    href="https://khultstudios.com/interior-photography-portfolio"
                />

                {/* Open Graph */}
                <meta property="og:type" content="website" />
                <meta
                    property="og:url"
                    content="https://khultstudios.com/interior-photography-portfolio"
                />
                <meta
                    property="og:title"
                    content="Interior Photography Portfolio in Hyderabad | KhultStudios"
                />
                <meta
                    property="og:description"
                    content="Browse our portfolio of interior and architectural photography featuring luxury homes, commercial spaces, hotels, restaurants, offices, and real estate projects."
                />
                <meta
                    property="og:image"
                    content="https://khultstudios.com/images/interior-photography-portfolio-banner.jpg"
                />
                <meta property="og:site_name" content="KhultStudios" />

                {/* Twitter */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta
                    name="twitter:title"
                    content="Interior Photography Portfolio | KhultStudios"
                />
                <meta
                    name="twitter:description"
                    content="Explore our portfolio of architectural, interior, hospitality, and commercial photography in Hyderabad."
                />
                <meta
                    name="twitter:image"
                    content="https://khultstudios.com/images/interior-photography-portfolio-banner.jpg"
                />

                {/* Favicon */}
                <link rel="icon" href="https://khultstudios.com/favicon.ico" />
            </Helmet>
            {/* FULLSCREEN VIDEO VIEWPORT BACKGROUND ENGINE */}
            <div className="video-bg-viewport">
                <video
                    autoPlay
                    loop
                    muted
                    playsInline
                    className="global-html5-video"
                >
                    <source src="videos/interior_architectural_reel.mp4" type="video/mp4" />
                    Your browser does not support the video tag.
                </video>
                <div className="vignette-overlay-layer" />
            </div>

            {/* INNER APP CANVAS CONTAINER SECTION */}
            <div className="page-frame px-2 px-md-5 pb-5">
                <div className="landing-container position-relative overflow-hidden">
                    <div className="paper-texture" />
                    <div className="container-fluid h-100 p-4 p-lg-5 position-relative z-2">
                        <div className="row h-100 gy-5">

                            {/* Content Brief side description */}
                            <div className="col-lg-5 d-flex flex-column justify-content-center h-100 content-side">
                                <div className="invitation-text position-relative py-4">
                                    <h5 className="text-uppercase tracking-wider text-orange fw-bold mb-2">
                                        Architectural & High-End Interior Spatial Designers
                                    </h5>
                                    <h1 className="display-3 text-uppercase main-title text-white mb-4">
                                        Capturing Form, Flow, and Symmetry
                                    </h1>
                                    <p className="lead-text text-light-gray mb-4 col-xl-10">
                                        An exceptional room is a multi-sensory experience. It holds a distinct atmosphere, a tactile balance of textures, and a deliberate geometric line composition. Stale, improperly leveled, or blindly wide-angle camera framing compresses spaces into flat, cold blocks—destroying structural intent and design value completely.
                                    </p>
                                    <p className="lead-text text-light-gray mb-4 col-xl-10">
                                        At KhultStudio, we specialize in high-fidelity interior and architectural asset design. By integrating multi-exposure ambient blending, strict parallax vertical leveling adjustments, and deliberate light path tracking, we craft atmospheric galleries that translate spatial dimensions into profound consumer desire.
                                    </p>
                                    <a
                                        href="#details"
                                        className="btn btn-orange text-uppercase px-4 py-2 text-dark fw-bold tracking-wide btn-scroll"
                                    >
                                        Explore Portfolios
                                    </a>
                                </div>
                                <div className="social-footer d-flex gap-3 mt-2">
                                    <a href="#" className="social-icon"><i className="fab fa-facebook-f" /></a>
                                    <a href="#" className="social-icon"><i className="fab fa-instagram" /></a>
                                    <a href="#" className="social-icon"><i className="fab fa-pinterest" /></a>
                                </div>
                            </div>

                            {/* Infinite Slanted Ribbon Gallery Strip components */}
                            <div className="col-lg-7 position-relative photo-zone-height">
                                <div className="filmstrip-skew-box">
                                    <div
                                        className="vertical-scroll-viewport"
                                        ref={viewportRef}
                                    >
                                        <div className="row gx-4 h-100">
                                            <div className="col-6">
                                                <div className="filmstrip-ribbon" ref={leftStripRef} style={{ display: 'none' }}>
                                                    <div className="ribbon-cell">
                                                        <img src="/interior-photography/interior-photography.jpg" alt="Wedding 5" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/interior-photography/interior-photography1.jpg" alt="Wedding 6" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/interior-photography/interior-photography2.jpg" alt="Wedding 7" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/interior-photography/interior-photography3.jpg" alt="Wedding 8" />
                                                    </div>
                                                    {/* Clone Buffers */}
                                                    <div className="ribbon-cell">
                                                        <img src="/interior-photography/interior-photography.jpg" alt="Wedding 5 Buffer" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/interior-photography/interior-photography1.jpg" alt="Wedding 6 Buffer" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/interior-photography/interior-photography2.jpg" alt="Wedding 7 Buffer" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/interior-photography/interior-photography3.jpg" alt="Wedding 8 Buffer" />
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-6">
                                                <div className="filmstrip-ribbon ribbon-offset" ref={rightStripRef}>
                                                    <div className="ribbon-cell">
                                                        <img src="/interior-photography/interior-photography.jpg" alt="Wedding 5" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/interior-photography/interior-photography1.jpg" alt="Wedding 6" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/interior-photography/interior-photography2.jpg" alt="Wedding 7" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/interior-photography/interior-photography3.jpg" alt="Wedding 8" />
                                                    </div>
                                                    {/* Clone Buffers */}
                                                    <div className="ribbon-cell">
                                                        <img src="/interior-photography/interior-photography.jpg" alt="Wedding 5 Buffer" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/interior-photography/interior-photography1.jpg" alt="Wedding 6 Buffer" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/interior-photography/interior-photography2.jpg" alt="Wedding 7 Buffer" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/interior-photography/interior-photography3.jpg" alt="Wedding 8 Buffer" />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                        </div>
                    </div>
                </div>
            </div>

            {/* INTERIOR ARCHITECTURE SERVICES SECTION */}
            <section className="services-section py-5" id="details">
                <div className="container">
                    <h2 className="text-center mb-5 text-white">🏢 Specialized Spatial Representation Classifications</h2>
                    <p className="text-light-gray mt-3 text-center max-w-2xl mx-auto">
                        Different layout scopes dictate unique lighting matrices. Our spatial capture systems adapt completely to structural parameters, keeping parallel alignments crisp across print, web, and luxury portfolio layers.
                    </p>
                    <div className="row g-4 mt-2">
                        {interiorServices.map((service, index) => (
                            <div key={index} className="col-md-4">
                                <div className={`service-card ${index === 0 ? 'active' : ''}`}>
                                    <div className="card-content">
                                        <service.icon className="icon mb-3" size={40} />
                                        <h3>{service.title}</h3>
                                        <p>{service.desc}</p>
                                    </div>
                                    <span className="card-number">{service.id}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* TECHNICAL PIPELINES & STRUCTURAL SAFEGUARDS */}
            <section className="comprehensive-section py-5 bg-black-opacity-50">
                <div className="container">
                    <h2 className="text-center mb-5 text-white">⚙️ Technical Execution & Precision Asset Pipelines</h2>
                    <div className="row g-4">
                        {interiorDeliverables.map((service, index) => (
                            <div key={index} className="col-lg-4 col-md-6">
                                <div className="service-card">
                                    <div className="card-content">
                                        <service.icon className="icon mb-3" size={40} />
                                        <h4>{service.title}</h4>
                                        <p>{service.desc}</p>
                                    </div>
                                    <span className="card-number">{service.id}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
};

export default InteriorPhotographyPortfolio;